/**
 * **把 BGM 策略接到真正的指挥器上。**
 *
 * `ui/bgm.ts` 只管策略（什么时候 load / start / 被拦了怎么办），
 * 这个文件是那半边**唯一**碰 Tone 和音频素材的地方 —— 于是策略能在 node 里
 * 用假 port 测，而不用把 Tone 拖进测试环境。
 */

import { BgmDirector } from './BgmDirector';
import { createZenithScore, zenithParams, type ZenithBgmParams } from './score.zenith';
import { PRE_GAME_BGM_PARAMS, type BgmPort } from '../ui/bgm';

/**
 * 造一个真的指挥器，并把它包成 `BgmPort`。
 *
 * ⚠️ 初值是 `PRE_GAME_BGM_PARAMS`（大厅曲 / 不濒死 / 无装饰层），
 * **显式传**而不是靠"参数缺省时规则恰好落到 lobby"：
 * 后者能工作只是巧合 —— 一旦 `score.zenith.ts` 的规则顺序变了，
 * 菜单就会在开局前响起终局曲，而且没有任何地方会报错。
 */
export function createZenithBgmPort(
  initial: ZenithBgmParams = PRE_GAME_BGM_PARAMS,
): BgmPort & { readonly director: BgmDirector } {
  const director = new BgmDirector(createZenithScore(), zenithParams(initial));
  return {
    director,
    load: () => director.load(),
    start: () => director.start(),
    // 游戏参数 → 引擎的 ParamBag 只在这里映射一次，调用方不用认识那些字符串
    setParams: (params: ZenithBgmParams) => director.setParams(zenithParams(params)),
    setVolume: (value: number) => director.setVolume(value),
    setFx: (amount: number) => director.setFx(amount),
    setBoost: (factor: number) => director.setBoost(factor),
  };
}
